import { useState, useEffect } from 'react'
import { api } from '../api/client'

interface WebhookEvent {
  eventId: number
  platform: string | null
  eventType: string | null
  senderId: string | null
  recipientId: string | null
  connectionId: number | null
  conversationType: string | null
  processedAt: string | null
  receivedAt: string
  payloadPreview: string | null
}

interface EventsResponse {
  count: number
  minutes: number
  source?: string
  events: WebhookEvent[]
}

export default function WebhookEvents() {
  const [events, setEvents] = useState<WebhookEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [minutes, setMinutes] = useState(120)
  const [limit, setLimit] = useState(50)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [source, setSource] = useState<'prod' | 'local'>('prod')
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

  const loadEvents = async (src?: 'prod' | 'local') => {
    const s = src ?? source
    setLoading(true)
    setError(null)
    try {
      const endpoint = s === 'prod' ? '/webhooks/events-prod' : '/webhooks/events'
      const res = await api.get<EventsResponse>(`${endpoint}?minutes=${minutes}&limit=${limit}`)
      setEvents(res.events)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  const replayFromProd = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ replayed: number }>(`/webhooks/replay-from-prod?minutes=${minutes}&limit=${limit}`)
      alert(`Replayed ${res.replayed} events from prod into local inbox`)
      await loadEvents()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Replay failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadEvents() }, [])

  return (
    <div>
      <h1>Webhook Events</h1>
      <p className="smm-hint">
        View raw webhook events stored in the database. Shows what Meta/X are actually sending to the endpoints.
      </p>

      <div className="smm-card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #ccc' }}>
            <button onClick={() => { setSource('prod'); loadEvents('prod') }}
              style={{ padding: '4px 12px', fontSize: '0.82rem', border: 'none', cursor: 'pointer',
                background: source === 'prod' ? '#1976d2' : '#fff',
                color: source === 'prod' ? '#fff' : '#333', fontWeight: source === 'prod' ? 600 : 400 }}>
              Production
            </button>
            <button onClick={() => { setSource('local'); loadEvents('local') }}
              style={{ padding: '4px 12px', fontSize: '0.82rem', border: 'none', cursor: 'pointer',
                borderLeft: '1px solid #ccc',
                background: source === 'local' ? '#1976d2' : '#fff',
                color: source === 'local' ? '#fff' : '#333', fontWeight: source === 'local' ? 600 : 400 }}>
              Local
            </button>
          </div>
          <label style={{ fontSize: '0.85rem' }}>
            Last
            <input type="number" className="smm-input" value={minutes}
              onChange={e => setMinutes(Number(e.target.value))}
              style={{ width: 60, margin: '0 4px', padding: '3px 6px' }} />
            min
          </label>
          <label style={{ fontSize: '0.85rem' }}>
            Limit
            <input type="number" className="smm-input" value={limit}
              onChange={e => setLimit(Number(e.target.value))}
              style={{ width: 60, margin: '0 4px', padding: '3px 6px' }} />
          </label>
          <button className="smm-btn smm-btn-primary smm-btn-sm" onClick={() => loadEvents()} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {isLocal && (
            <button className="smm-btn smm-btn-sm"
              style={{ background: '#fff3e0', border: '1px solid #ff9800' }}
              onClick={replayFromProd} disabled={loading}>
              Replay to Local Inbox
            </button>
          )}
        </div>
      </div>

      {error && <div className="smm-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {events.length === 0 && !loading && !error && (
        <div className="smm-card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p>No events loaded. Click "Load Events" to query the database.</p>
        </div>
      )}

      {events.length > 0 && (
        <div className="smm-card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--smm-bg, #f8f9fa)', borderBottom: '2px solid var(--smm-border, #dee2e6)' }}>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Platform</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Sender</th>
                <th style={thStyle}>Recipient</th>
                <th style={thStyle}>Conn</th>
                <th style={thStyle}>Conv Type</th>
                <th style={thStyle}>Received</th>
                <th style={thStyle}>Processed</th>
                <th style={thStyle}>Payload</th>
              </tr>
            </thead>
            <tbody>
              {events.map(e => (
                <tr key={e.eventId} style={{ borderBottom: '1px solid var(--smm-border, #eee)' }}
                  onClick={() => setExpanded(expanded === e.eventId ? null : e.eventId)}>
                  <td style={tdStyle}>{e.eventId}</td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 600 }}>{e.platform || '-'}</span>
                  </td>
                  <td style={tdStyle}>{e.eventType || '-'}</td>
                  <td style={{ ...tdStyle, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.senderId || '-'}
                  </td>
                  <td style={{ ...tdStyle, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.recipientId || '-'}
                  </td>
                  <td style={tdStyle}>{e.connectionId ?? '-'}</td>
                  <td style={tdStyle}>{e.conversationType || '-'}</td>
                  <td style={tdStyle}>{formatTime(e.receivedAt)}</td>
                  <td style={tdStyle}>
                    {e.processedAt
                      ? <span style={{ color: '#2e7d32' }}>{formatTime(e.processedAt)}</span>
                      : <span style={{ color: '#e65100' }}>Pending</span>}
                  </td>
                  <td style={{ ...tdStyle, cursor: 'pointer', color: '#1976d2' }}>
                    {e.payloadPreview ? 'View' : '-'}
                  </td>
                </tr>
              ))}
              {events.length > 0 && expanded != null && (() => {
                const ev = events.find(e => e.eventId === expanded)
                if (!ev?.payloadPreview) return null
                return (
                  <tr>
                    <td colSpan={10} style={{ padding: '0.75rem', background: '#f5f5f5' }}>
                      <strong>Payload preview (event #{expanded}):</strong>
                      <pre style={{
                        margin: '0.5rem 0 0', padding: '0.5rem', background: '#fff',
                        border: '1px solid #ddd', borderRadius: 4, fontSize: '0.78rem',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 300, overflow: 'auto'
                      }}>
                        {formatJson(ev.payloadPreview)}
                      </pre>
                    </td>
                  </tr>
                )
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const thStyle: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 600,
}
const tdStyle: React.CSSProperties = {
  padding: '6px 10px', whiteSpace: 'nowrap', verticalAlign: 'top',
}

function formatTime(iso: string) {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      + ' ' + d.toLocaleDateString([], { month: 'short', day: 'numeric' })
  } catch { return iso }
}

function formatJson(s: string) {
  try { return JSON.stringify(JSON.parse(s), null, 2) } catch { return s }
}
